"use client"

import { useEffect } from "react"
import { FormikProvider, useFormik } from "formik"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { FormInput, FormSelect } from "@/components/form-inputs"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { ImageUpload } from "@/components/ui/image-upload"
import { Switch } from "@/components/ui/switch"
import { Loader2 } from "lucide-react"
import { useUpdateTenant } from "@/hooks/use-update-tenant"
import type { AIBillingMode } from "@/lib/ai/langchain/types"
import { useTranslations } from "next-intl"


function getBILLING_MODE_OPTIONS(tUi: ReturnType<typeof useTranslations<"TenantManagement.TenantManagementTenantEditDialog">>) {
  return [
  { value: "tenant", label: tUi("tenantWallet") },
  { value: "platform", label: tUi("platform") },
] as const satisfies readonly { value: AIBillingMode; label: string }[]
}

interface TenantEditDialogProps {
  tenant: {
    id: string
    name: string
    logo_url: string | null
    disable_free_plan_expiry: boolean
    partner_program_enabled: boolean | null
    is_active: boolean
    archived_at: string | null
    conversation_quality_platform_enabled: boolean
    conversation_quality_billing_mode: AIBillingMode
  } | null
  onOpenChange: (open: boolean) => void
  canEditTenantDetails: boolean
  canChangeTenantStatus: boolean
  isPlatformTenant: boolean
  onReactivated: (tenant: { id: string; name: string }) => void
  onUpdated: (updated: {
    id: string
    name?: string
    logo_url?: string | null
    disable_free_plan_expiry?: boolean
    partner_program_enabled?: boolean | null
    is_active?: boolean
    conversation_quality_platform_enabled?: boolean
    conversation_quality_billing_mode?: AIBillingMode
  }) => void
}

interface TenantEditValues {
  name: string
  disable_free_plan_expiry: boolean
  partner_program: PartnerProgramOption
  is_active: boolean
  conversation_quality_platform_enabled: boolean
  conversation_quality_billing_mode: AIBillingMode
}

type PartnerProgramOption = "default" | "enabled" | "disabled"

function getPARTNER_PROGRAM_OPTIONS(tUi: ReturnType<typeof useTranslations<"TenantManagement.TenantManagementTenantEditDialog">>): readonly { value: PartnerProgramOption; label: string }[] {
  return [
  { value: "default", label: tUi("platformDefault") },
  { value: "enabled", label: tUi("enabled") },
  { value: "disabled", label: tUi("disabled") },
]
}

function toPartnerProgramOption(override: boolean | null): PartnerProgramOption {
  switch (override) {
    case true:
      return "enabled"
    case false:
      return "disabled"
    default:
      return "default"
  }
}

function toPartnerProgramOverride(option: PartnerProgramOption): boolean | null {
  switch (option) {
    case "enabled":
      return true
    case "disabled":
      return false
    default:
      return null
  }
}

export function TenantEditDialog({
  tenant,
  onOpenChange,
  canEditTenantDetails,
  canChangeTenantStatus,
  isPlatformTenant,
  onReactivated,
  onUpdated,
}: TenantEditDialogProps) {
  const tUi = useTranslations("TenantManagement.TenantManagementTenantEditDialog")
  const BILLING_MODE_OPTIONS = getBILLING_MODE_OPTIONS(tUi)
  const PARTNER_PROGRAM_OPTIONS = getPARTNER_PROGRAM_OPTIONS(tUi)

  const tCommonUi = useTranslations("Common")

  const { isSaving, isUploadingLogo, error, updateTenant, uploadLogo, removeLogo, resetError } =
    useUpdateTenant()
  const formik = useFormik<TenantEditValues>({
    enableReinitialize: true,
    initialValues: {
      name: tenant?.name ?? "",
      disable_free_plan_expiry: tenant?.disable_free_plan_expiry ?? false,
      partner_program: toPartnerProgramOption(tenant?.partner_program_enabled ?? null),
      is_active: tenant?.is_active ?? false,
      conversation_quality_platform_enabled: tenant?.conversation_quality_platform_enabled ?? true,
      conversation_quality_billing_mode: tenant?.conversation_quality_billing_mode ?? "tenant",
    },
    onSubmit: async (values) => {
      if (!tenant) return
      const nameChanged = values.name.trim() !== tenant.name
      const expirySettingChanged =
        values.disable_free_plan_expiry !== tenant.disable_free_plan_expiry
      const partnerProgramOverride = toPartnerProgramOverride(values.partner_program)
      const partnerProgramChanged = partnerProgramOverride !== tenant.partner_program_enabled
      const statusChanged = values.is_active !== tenant.is_active
      const canSaveName = canEditTenantDetails && nameChanged
      const canSaveStatus = canChangeTenantStatus && statusChanged
      const qualityPatch = canEditTenantDetails ? qualityChanges(values, tenant) : {}
      const tenantReactivated =
        canSaveStatus && tenant.is_active === false && values.is_active === true
      if (
        !canSaveName &&
        !expirySettingChanged &&
        !partnerProgramChanged &&
        !canSaveStatus &&
        Object.keys(qualityPatch).length === 0
      ) {
        return
      }

      const patch = {
        ...(canSaveName ? { name: values.name.trim() } : {}),
        ...(expirySettingChanged
          ? { disable_free_plan_expiry: values.disable_free_plan_expiry }
          : {}),
        ...(partnerProgramChanged ? { partner_program_enabled: partnerProgramOverride } : {}),
        ...(canSaveStatus ? { is_active: values.is_active } : {}),
        ...qualityPatch,
      }
      try {
        await updateTenant(tenant.id, patch)
        onUpdated({ id: tenant.id, ...patch })
        if (tenantReactivated) {
          onReactivated({ id: tenant.id, name: tenant.name })
        }
      } catch {
        /* error is surfaced via the hook's `error` state */
      }
    },
  })

  useEffect(() => {
    if (tenant) {
      resetError()
    }
  }, [tenant, resetError])

  const nameChanged = tenant ? formik.values.name.trim() !== tenant.name : false
  const expirySettingChanged = tenant
    ? formik.values.disable_free_plan_expiry !== tenant.disable_free_plan_expiry
    : false
  const partnerProgramChanged = tenant
    ? toPartnerProgramOverride(formik.values.partner_program) !== tenant.partner_program_enabled
    : false
  const statusChanged = tenant
    ? formik.values.is_active !== tenant.is_active
    : false
  const qualityChanged = tenant
    ? Object.keys(qualityChanges(formik.values, tenant)).length > 0
    : false
  const hasChanges =
    (canEditTenantDetails && nameChanged) ||
    expirySettingChanged ||
    partnerProgramChanged ||
    (canChangeTenantStatus && statusChanged) ||
    (canEditTenantDetails && qualityChanged)

  const handleLogoUpload = async (file: File) => {
    if (!tenant) return
    try {
      const { url } = await uploadLogo(tenant.id, file)
      onUpdated({ id: tenant.id, logo_url: url })
    } catch (err) {
      console.error("Logo upload error:", err)
    }
  }

  const handleLogoRemove = async () => {
    if (!tenant) return
    try {
      await removeLogo(tenant.id)
      onUpdated({ id: tenant.id, logo_url: null })
    } catch (err) {
      console.error("Logo remove error:", err)
    }
  }

  return (
    <Dialog open={!!tenant} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <span key={canEditTenantDetails ? tUi("editTenant") : tUi("tenantSettings")}>{canEditTenantDetails ? tUi("editTenant") : tUi("tenantSettings")}</span>
          </DialogTitle>
          <DialogDescription>
            {tenant?.id.slice(0, 8)}<span>...</span>
          </DialogDescription>
        </DialogHeader>

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className="space-y-4">
              {canEditTenantDetails && (
                <FormInput
                  name="name"
                  label={tUi("name")}
                  placeholder={tUi("tenantName")}
                />
              )}

              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="disable-free-plan-expiry">
                    <span key={tUi("disableFreePlanExpiry")}>{tUi("disableFreePlanExpiry")}</span></Label>
                  <p className="text-xs text-muted-foreground">
                    <span key={tUi("excludeThisTenantFromPlanExpiryAndHide")}>{tUi("excludeThisTenantFromPlanExpiryAndHide")}</span></p>
                </div>
                <Switch
                  id="disable-free-plan-expiry"
                  checked={formik.values.disable_free_plan_expiry}
                  onCheckedChange={(checked) =>
                    void formik.setFieldValue("disable_free_plan_expiry", checked)
                  }
                />
              </div>

              {canChangeTenantStatus && (
                <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="tenant-active"><span key={tUi("tenantActive")}>{tUi("tenantActive")}</span></Label>
                    <p className="text-xs text-muted-foreground">
                      {getTenantStatusDescription(
                        tenant?.archived_at ?? null,
                        isPlatformTenant, tUi
                      )}
                    </p>
                  </div>
                  <Switch
                    id="tenant-active"
                    checked={formik.values.is_active}
                    onCheckedChange={(checked) =>
                      void formik.setFieldValue("is_active", checked)
                    }
                    disabled={
                      !!tenant?.archived_at ||
                      (isPlatformTenant && tenant?.is_active === true)
                    }
                  />
                </div>
              )}

              <FormSelect
                name="partner_program"
                label={tUi("partnerProgram")}
                options={PARTNER_PROGRAM_OPTIONS}
                description={tUi("platformDefaultFollowsThePartnerProgramSwitchIn")}
              />

              {canEditTenantDetails && (
                <div className="space-y-3 rounded-md border p-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="conversation-quality-enabled"><span key={tUi("conversationQuality")}>{tUi("conversationQuality")}</span></Label>
                      <p className="text-xs text-muted-foreground">
                        <span key={tUi("offTheTenantCannotTurnItOnAnd")}>{tUi("offTheTenantCannotTurnItOnAnd")}</span></p>
                    </div>
                    <Switch
                      id="conversation-quality-enabled"
                      checked={formik.values.conversation_quality_platform_enabled}
                      onCheckedChange={(checked) =>
                        void formik.setFieldValue("conversation_quality_platform_enabled", checked)
                      }
                    />
                  </div>
                  <FormSelect
                    name="conversation_quality_billing_mode"
                    label={tUi("paidBy")}
                    options={BILLING_MODE_OPTIONS}
                    description={tUi("whoPaysForItsModelCallsAChange")}
                  />
                </div>
              )}

              {canEditTenantDetails && (
                <div className="space-y-2">
                  <Label><span key={tUi("logo")}>{tUi("logo")}</span></Label>
                  <ImageUpload
                    currentImageUrl={tenant?.logo_url ?? null}
                    onUpload={handleLogoUpload}
                    onRemove={handleLogoRemove}
                    isUploading={isUploadingLogo}
                    shape="circle"
                  />
                </div>
              )}

              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
              <Button type="submit" disabled={!hasChanges || isSaving}>
                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <span><span key={tUi("save")}>{tUi("save")}</span></span>
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

/** The quality fields whose value differs from the tenant's, as the PATCH takes them. */
function qualityChanges(
  values: TenantEditValues,
  tenant: NonNullable<TenantEditDialogProps["tenant"]>
): {
  conversation_quality_platform_enabled?: boolean
  conversation_quality_billing_mode?: AIBillingMode
} {
  return {
    ...(values.conversation_quality_platform_enabled !== tenant.conversation_quality_platform_enabled
      ? { conversation_quality_platform_enabled: values.conversation_quality_platform_enabled }
      : {}),
    ...(values.conversation_quality_billing_mode !== tenant.conversation_quality_billing_mode
      ? { conversation_quality_billing_mode: values.conversation_quality_billing_mode }
      : {}),
  }
}

function getTenantStatusDescription(
  archivedAt: string | null,
  platformTenant: boolean,tUi: ReturnType<typeof useTranslations<"TenantManagement.TenantManagementTenantEditDialog">>
): string {
  if (archivedAt) {
    return tUi("restoreThisTenantBeforeChangingItsActiveStatus")
  }
  if (platformTenant) {
    return tUi("thePlatformTenantCannotBeDeactivated")
  }
  return tUi("inactiveTenantsCannotUseTheirWorkspaceUntilReactivated")
}
